/**
 * The ad group's delivery fields as the wizard asks for them — frequency cap and its window,
 * audience expansion, standard or accelerated delivery. The screen's half of
 * `apps/worker/src/platform/line-item-delivery.ts` (PR 6 of audits/2026-10-23/all-fields-spec.md).
 *
 * 🔴 The lists are the worker's, copied, and `line-item-delivery.test.ts` reads the worker's
 * file to hold them equal. A field left empty is not sent — no default of X's is filled in
 * (the spec: «Defaults X не підставляємо»).
 *
 * Not wired into the wizard yet: the worker's route takes these after the queue ahead of
 * PR 6 merges (lead f5, 2026-10-24).
 */
import type { LineItemObjective } from "./api.ts";

/** The objectives the cap is offered under — copied from the worker's `FREQUENCY_CAP_OBJECTIVES`. */
export const FREQUENCY_CAP_OBJECTIVES: readonly LineItemObjective[] = ["ENGAGEMENTS"];

/** The cap's window — copied from the worker's `DURATION_IN_DAYS`. */
export const DURATION_IN_DAYS = [1, 7, 30] as const;

/** Copied from the worker's `AUDIENCE_EXPANSION`. */
export const AUDIENCE_EXPANSION = ["BROAD", "DEFINED", "EXPANDED"] as const;

/** The block as typed: text until sent, `""` meaning «not sent». */
export interface DeliveryDraft {
	frequencyCap: string;
	durationInDays: string;
	audienceExpansion: string;
	/** `""` not sent; `"standard"` / `"accelerated"` send true / false. */
	pacing: "" | "standard" | "accelerated";
}

export const EMPTY_DELIVERY: DeliveryDraft = {
	frequencyCap: "",
	durationInDays: "",
	audienceExpansion: "",
	pacing: "",
};

export type DeliveryProblem = "cap-without-window" | "window-without-cap" | "cap-invalid";

/** Whether the cap is shown at all: only under an objective X lists for it (R:3126). */
export function capOffered(objective: LineItemObjective): boolean {
	return FREQUENCY_CAP_OBJECTIVES.includes(objective);
}

/**
 * The block's own problem, as the worker would refuse it: the cap and its window go together
 * (this console's rule — the reference names none), and the cap is a whole number of 1 or
 * more. Under an objective without the cap the two fields are not asked, so not checked.
 */
export function deliveryProblem(
	objective: LineItemObjective,
	d: DeliveryDraft,
): DeliveryProblem | null {
	if (!capOffered(objective)) return null;
	const cap = d.frequencyCap.trim();
	if (cap !== "" && d.durationInDays === "") return "cap-without-window";
	if (cap === "" && d.durationInDays !== "") return "window-without-cap";
	if (cap !== "" && !/^[1-9][0-9]*$/.test(cap)) return "cap-invalid";
	return null;
}

/** The body's delivery keys — only the ones given, and the cap only where it is offered. */
export function deliveryBody(
	objective: LineItemObjective,
	d: DeliveryDraft,
): {
	frequencyCap?: number;
	durationInDays?: number;
	audienceExpansion?: string;
	standardDelivery?: boolean;
} {
	const out: ReturnType<typeof deliveryBody> = {};
	if (capOffered(objective) && d.frequencyCap.trim() !== "" && d.durationInDays !== "") {
		out.frequencyCap = Number(d.frequencyCap.trim());
		out.durationInDays = Number(d.durationInDays);
	}
	if (d.audienceExpansion !== "") out.audienceExpansion = d.audienceExpansion;
	if (d.pacing !== "") out.standardDelivery = d.pacing === "standard";
	return out;
}
